import React, {
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

export interface TutorialStep {
  id: string;
  title: string;
  description: string;
  image?: string;
  position?: 'right' | 'bottom' | 'auto';
  selector?: string; // CSS selector to target existing elements
}

interface InteractiveTutorialProps {
  steps: TutorialStep[];
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
  currentStepIndex?: number;
  children: ReactNode;
  buttonTexts?: {
    back?: string;
    next?: string;
    continue?: string;
    complete?: string;
  };
}

interface TutorialContextType {
  currentStepId: string | null;
  registerStep: (id: string, element: HTMLElement) => void;
  unregisterStep: (id: string) => void;
}

export const TutorialContext = React.createContext<TutorialContextType>({
  currentStepId: null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerStep: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  unregisterStep: () => {},
});

const InteractiveTutorial: React.FC<InteractiveTutorialProps> = ({
  steps,
  isActive,
  onComplete,
  onSkip,
  currentStepIndex = 0,
  children,
  buttonTexts = {},
}) => {
  const [currentStep, setCurrentStep] = useState(0); // Always start from first step
  const [registeredElements, setRegisteredElements] = useState<
    Map<string, HTMLElement>
  >(new Map());
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Sync internal state with external currentStepIndex prop
  useEffect(() => {
    setCurrentStep(currentStepIndex);
  }, [currentStepIndex]);

  // Reset internal state when tutorial becomes active
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0); // Always reset to first step when tutorial starts
    }
  }, [isActive]);

  const currentStepData = steps[currentStep];

  // Get element either from registered elements or by selector
  const getCurrentElement = (): HTMLElement | null => {
    if (!currentStepData) return null;

    // First try registered elements
    const registeredElement = registeredElements.get(currentStepData.id);
    if (registeredElement) return registeredElement;

    // Then try selector(s) - handle multiple selectors separated by commas
    if (currentStepData.selector) {
      const selectors = currentStepData.selector
        .split(',')
        .map((s) => s.trim());
      for (const selector of selectors) {
        const element = document.querySelector(selector) as HTMLElement;
        if (element) return element;
      }
    }

    return null;
  };

  const currentElement = getCurrentElement();
  const [modalPosition, setModalPosition] = useState<{
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
    transform?: string;
  }>({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const [pointerConfig, setPointerConfig] = useState<
    { style: any; className: string } | { display: string }
  >({ display: 'none' });
  const [isScrolling, setIsScrolling] = useState(false);
  const [showHighlighter, setShowHighlighter] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [modalDimensions, setModalDimensions] = useState({
    width: 384,
    height: 400,
  });
  const [zoomFactor, setZoomFactor] = useState(1);

  // Calculate zoom factor based on browser zoom level
  const calculateZoomFactor = useCallback(() => {
    if (window.visualViewport) {
      // At 25% browser zoom: visualViewport.scale = 4.0 → factor = 4.0
      // At 100% browser zoom: visualViewport.scale = 1.0 → factor = 1.0
      // At 200% browser zoom: visualViewport.scale = 0.5 → factor = 0.5
      const scale = window.visualViewport.scale || 1;
      return scale;
    }
    return 1;
  }, []);

  // Calculate responsive modal dimensions based on screen size and zoom
  const calculateModalDimensions = useCallback(() => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Much smaller base dimensions for compact modal
    const baseWidth = 250;
    const baseHeight = 280;

    // Scale factors based on screen width
    let scaleFactor = 1;

    if (windowWidth < 768) {
      // Small screens (mobile-like desktop windows)
      scaleFactor = 0.6;
    } else if (windowWidth < 1024) {
      // Medium screens (tablet-like desktop windows)
      scaleFactor = 0.75;
    } else if (windowWidth < 1200) {
      // Smaller desktop screens
      scaleFactor = 0.85;
    }

    // Apply zoom factor - modal gets bigger when zoomed out, smaller when zoomed in
    const currentZoomFactor = calculateZoomFactor();
    scaleFactor = scaleFactor * currentZoomFactor;

    // Also consider height constraints
    const maxHeightRatio = windowHeight * 0.8; // Use max 80% of screen height
    const scaledHeight = baseHeight * scaleFactor;

    if (scaledHeight > maxHeightRatio) {
      scaleFactor = maxHeightRatio / baseHeight;
    }

    const width = Math.floor(baseWidth * scaleFactor);
    const height = Math.floor(baseHeight * scaleFactor);

    return { width, height, scaleFactor };
  }, [calculateZoomFactor]);

  // Update modal dimensions when screen size or zoom changes
  useEffect(() => {
    const updateDimensions = () => {
      const currentZoom = calculateZoomFactor();
      setZoomFactor(currentZoom);

      const dimensions = calculateModalDimensions();
      setModalDimensions({
        width: dimensions.width,
        height: dimensions.height,
      });
    };

    updateDimensions();

    // Listen to both window resize and visualViewport resize (for zoom changes)
    window.addEventListener('resize', updateDimensions);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDimensions);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDimensions);
      }
    };
  }, [calculateModalDimensions, calculateZoomFactor]);

  // Update modal position when element or step changes
  useEffect(() => {
    if (currentElement && isActive && !isScrolling) {
      // Fast position calculation for better user experience
      const updatePosition = () => {
        setModalPosition(getModalPosition());
        setPointerConfig(getPointerStyle());
        setShowHighlighter(true);
      };

      // Much faster delays - only wait for DOM to settle
      // Give extra time for the last step to ensure proper positioning
      const isIPad = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isLastStep = currentStep === steps.length - 1;
      let delay = isIPad ? 500 : 300; // Base dela18

      if (isLastStep) {
        // Add extra time for the final step
        delay = isIPad ? 1500 : 600; // Double the delay for last step
        console.log('Last step detected, using extended delay:', delay);
      }

      const timeoutId = setTimeout(updatePosition, delay);

      return () => clearTimeout(timeoutId);
    } else if (isScrolling) {
      // Hide highlighter and modal during scroll for all steps
      setShowHighlighter(false);
      setShowModal(false);
    }
  }, [currentElement, currentStep, isActive, isScrolling, modalDimensions]);

  // Add resize listener to recalculate modal position on window resize
  useEffect(() => {
    if (!isActive || !currentElement) return;

    const handleResize = () => {
      // Hide modal temporarily during resize
      setShowModal(false);
      setShowHighlighter(false);

      // Recalculate position after a short delay
      // Give extra time for the last step during resize
      const isLastStep = currentStep === steps.length - 1;
      const resizeDelay = isLastStep ? 250 : 100; // Extended delay for last step

      const timeoutId = setTimeout(() => {
        if (currentElement && isActive && !isScrolling) {
          setModalPosition(getModalPosition());
          setPointerConfig(getPointerStyle());
          setShowHighlighter(true);
          setShowModal(true);
        }
      }, resizeDelay);

      return () => clearTimeout(timeoutId);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, currentElement, isScrolling, modalDimensions]);

  // Show loading animation immediately when step changes
  useEffect(() => {
    if (isActive && currentStepData) {
      setShowLoadingAnimation(true);
      setShowHighlighter(false);
      setShowModal(false);
    }
  }, [currentStep, isActive, currentStepData]);

  // Watch for highlighter visibility and show modal after highlighting is complete
  useEffect(() => {
    if (showHighlighter && isActive && currentElement) {
      // Small delay to ensure highlighter transition completes before showing modal
      // Give extra time for the last step to ensure proper positioning
      const isLastStep = currentStep === steps.length - 1;
      const modalDelay = isLastStep ? 650 : 250; // Extended delay for last step

      const modalTimeout = setTimeout(() => {
        setShowModal(true);
        setShowLoadingAnimation(false); // Hide loading when modal appears
        if (isLastStep) {
          console.log('Last step modal shown with extended delay');
        }
      }, modalDelay);

      return () => clearTimeout(modalTimeout);
    } else {
      // Hide modal when highlighter is not visible
      setShowModal(false);
    }
  }, [showHighlighter, isActive, currentElement]);

  const registerStep = useCallback((id: string, element: HTMLElement) => {
    setRegisteredElements((prev) => {
      const newMap = new Map(prev);
      newMap.set(id, element);
      return newMap;
    });
  }, []);

  const unregisterStep = useCallback((id: string) => {
    setRegisteredElements((prev) => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const getModalPosition = () => {
    if (!currentElement)
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    // Wait for scroll to complete before calculating position
    const rect = currentElement.getBoundingClientRect();
    const modalWidth = modalDimensions.width;
    const modalHeight = modalDimensions.height;
    // Account for pointer size and highlight border to prevent overlap
    const highlightBorder = 8; // Highlight border size
    const pointerSize = 10; // Pointer width/height
    const baseSpacing = highlightBorder + pointerSize; // 18px total spacing
    const scaleFactor = modalWidth / 250; // Base width is 250px (compact modal)
    const padding = Math.max(
      baseSpacing,
      Math.floor(baseSpacing * scaleFactor)
    ); // Ensure modal touches pointer edge, not overlaps
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const position: {
      top?: string;
      left?: string;
      bottom?: string;
      right?: string;
      transform?: string;
    } = {};

    // Determine if we should show modal to the right or bottom
    const spaceRight = windowWidth - rect.right;
    const spaceBottom = windowHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceTop = rect.top;

    // For sidebar elements (like More menu), prefer right positioning
    const isSidebarElement = rect.left < 100; // Sidebar is typically < 100px wide

    // Special case for "view all transactions" - always place above with extra spacing to avoid cropping
    if (currentStepData?.id === 'view-all-transactions') {
      // Scale extra spacing based on modal size - smaller modals need less spacing
      const scaleFactor = modalWidth / 250; // Base width is 280px (compact modal)
      const baseExtraSpacing = Math.floor(50 * scaleFactor); // Scale the extra spacing (compact)
      // Add minimum spacing to ensure no overlap
      const minSpacing = 8; // Minimum 12px gap (compact)
      const extraSpacing = Math.max(baseExtraSpacing, minSpacing);
      const totalSpacing = padding + extraSpacing;

      // Debug logging for troubleshooting
      console.log('View All Transactions Debug:', {
        windowSize: `${windowWidth}x${windowHeight}`,
        modalSize: `${modalWidth}x${modalHeight}`,
        elementRect: rect,
        scaleFactor,
        baseExtraSpacing,
        extraSpacing,
        totalSpacing,
        padding,
      });

      // Always try to place above first with extra spacing
      // Account for the highlighting border (8px) that extends above the element
      const highlightBorder = 8;
      const calculatedTop =
        rect.top - highlightBorder - modalHeight - totalSpacing;

      // Check if there's enough space above (need more generous space check)
      if (
        calculatedTop >= padding &&
        rect.top > modalHeight + totalSpacing + padding + highlightBorder
      ) {
        // Place above with guaranteed spacing
        position.top = `${calculatedTop}px`;
        position.left = `${Math.max(padding, Math.min(windowWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2))}px`;
        console.log(
          'Positioned above at:',
          calculatedTop,
          'accounting for highlight border'
        );
      } else {
        // Not enough space above, place below with scaled spacing
        const belowSpacing = Math.floor(totalSpacing * 0.5); // Reduced from 0.7 to 0.5 for tighter spacing
        position.top = `${rect.bottom + belowSpacing}px`;
        position.left = `${Math.max(padding, Math.min(windowWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2))}px`;

        // If placing below would go off screen, try positioning to the right instead
        if (rect.bottom + belowSpacing + modalHeight > windowHeight - padding) {
          // Try right positioning for better visibility
          const spaceRight = windowWidth - rect.right;
          if (spaceRight >= modalWidth + padding) {
            position.left = `${rect.right + padding}px`;
            position.top = `${Math.max(padding, Math.min(windowHeight - modalHeight - padding, rect.top - modalHeight / 2 + rect.height / 2))}px`;
            delete position.transform;
          } else {
            // Last resort: center the modal
            position.top = '50%';
            position.left = '50%';
            position.transform = 'translate(-50%, -50%)';
          }
        }
      }

      return position;
    }
    // Try right positioning first for sidebar elements or if there's enough space
    else if (
      (isSidebarElement || spaceRight >= modalWidth + padding) &&
      currentStepData?.position !== 'bottom'
    ) {
      position.left = `${rect.right + padding}px`;
      position.top = `${Math.max(padding, Math.min(windowHeight - modalHeight - padding, rect.top - modalHeight / 2 + rect.height / 2))}px`;
    }
    // Try left positioning if right doesn't work and we're not in sidebar
    else if (
      !isSidebarElement &&
      spaceLeft >= modalWidth + padding &&
      currentStepData?.position !== 'bottom'
    ) {
      position.left = `${rect.left - modalWidth - padding}px`;
      position.top = `${Math.max(padding, Math.min(windowHeight - modalHeight - padding, rect.top - modalHeight / 2 + rect.height / 2))}px`;
    }
    // Try bottom positioning
    else if (spaceBottom >= modalHeight + padding) {
      position.top = `${rect.bottom + padding}px`;
      position.left = `${Math.max(padding, Math.min(windowWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2))}px`;
    }
    // Try top positioning
    else if (spaceTop >= modalHeight + padding) {
      position.top = `${rect.top - modalHeight - padding}px`;
      position.left = `${Math.max(padding, Math.min(windowWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2))}px`;
    }
    // Fallback to center if no good position
    else {
      position.top = '50%';
      position.left = '50%';
      position.transform = 'translate(-50%, -50%)';
    }

    return position;
  };

  const getPointerStyle = () => {
    if (!currentElement) return { display: 'none' };

    const rect = currentElement.getBoundingClientRect();
    const modalWidth = modalDimensions.width;
    const modalHeight = modalDimensions.height;
    // Use same spacing calculation as modal positioning for consistency
    const highlightBorder = 8; // Highlight border size
    const pointerSize = 10; // Pointer width/height
    const baseSpacing = highlightBorder + pointerSize; // 18px total spacing
    const scaleFactor = modalWidth / 250; // Base width is 250px (compact modal)
    const padding = Math.max(
      baseSpacing,
      Math.floor(baseSpacing * scaleFactor)
    ); // Consistent spacing
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const spaceRight = windowWidth - rect.right;
    const spaceLeft = rect.left;
    const spaceBottom = windowHeight - rect.bottom;
    const spaceTop = rect.top;
    const isSidebarElement = rect.left < 100;

    const pointerStyle: any = { position: 'absolute', zIndex: 10002 };
    let pointerClass = '';

    // Determine pointer position based on modal placement logic
    // Special case for "view all transactions" - pointer positioned dynamically based on modal placement
    if (currentStepData?.id === 'view-all-transactions') {
      // Scale pointer gap based on modal size - ensure pointer only touches highlight border
      const scaleFactor = modalWidth / 250; // Base width is 250px (compact modal)
      const basePointerGap = Math.floor(12 * scaleFactor); // Increased gap to avoid overlap
      const minPointerGap = 8; // Minimum gap to ensure pointer only touches border
      const pointerGap = Math.max(basePointerGap, minPointerGap);

      const baseExtraSpacing = Math.floor(50 * scaleFactor); // Compact spacing
      const minSpacing = 8; // Minimum 12px gap (compact)
      const extraSpacing = Math.max(baseExtraSpacing, minSpacing);
      const totalSpacing = padding + extraSpacing;
      const highlightBorder = 8; // Account for highlighting border
      const calculatedTop =
        rect.top - highlightBorder - modalHeight - totalSpacing;

      // Check if modal is placed above or below the element (use same logic as positioning)
      if (
        calculatedTop >= padding &&
        rect.top > modalHeight + totalSpacing + padding + highlightBorder
      ) {
        // Modal is above, pointer points down to the element
        pointerStyle.left = `${rect.left + rect.width / 2 - 4}px`; // Centered horizontally with element (smaller pointer)
        // Position pointer to just touch the highlight border (8px border + pointer height)
        pointerStyle.top = `${rect.top - highlightBorder - 10}px`; // Just touching the highlight border
        pointerClass = 'pointer-down'; // Points down to the element
        console.log(
          'Pointer positioned above highlight at:',
          rect.top - highlightBorder - 10
        );
      } else {
        // Modal is below or positioned elsewhere - check positioning
        const belowSpacing = Math.floor(totalSpacing * 0.5); // Match the updated spacing
        if (rect.bottom + belowSpacing + modalHeight > windowHeight - padding) {
          // Check if modal is positioned to the right
          const spaceRight = windowWidth - rect.right;
          if (spaceRight >= modalWidth + padding) {
            // Modal is to the right, pointer points left
            pointerStyle.left = `${rect.right + highlightBorder}px`; // Just touching the highlight border on the right
            pointerStyle.top = `${rect.top + rect.height / 2 - 4}px`; // 4px = half pointer height (smaller pointer)
            pointerClass = 'pointer-left';
          } else {
            // Modal is centered, no pointer
            return { display: 'none' };
          }
        } else {
          // Modal is below, pointer points up to the element
          pointerStyle.left = `${rect.left + rect.width / 2 - 4}px`; // Centered horizontally with element (smaller pointer)
          pointerStyle.top = `${rect.bottom + highlightBorder}px`; // Just touching the highlight border below
          pointerClass = 'pointer-up'; // Points up to the element
        }
      }

      pointerStyle.zIndex = 10003; // Higher z-index to ensure visibility
      return { style: pointerStyle, className: pointerClass };
    } else if (
      (isSidebarElement || spaceRight >= modalWidth + padding) &&
      currentStepData?.position !== 'bottom'
    ) {
      // Modal is to the right, pointer points left
      pointerStyle.left = `${rect.right + 8}px`; // Just touching the highlight border (8px border)
      pointerStyle.top = `${rect.top + rect.height / 2 - 4}px`; // 4px = half pointer height (smaller pointer)
      pointerClass = 'pointer-left';
    } else if (
      !isSidebarElement &&
      spaceLeft >= modalWidth + padding &&
      currentStepData?.position !== 'bottom'
    ) {
      // Modal is to the left, pointer points right
      pointerStyle.left = `${rect.left - 8 - 10}px`; // Just touching the highlight border (8px border + 10px pointer width)
      pointerStyle.top = `${rect.top + rect.height / 2 - 4}px`; // 4px = half pointer height (smaller pointer)
      pointerClass = 'pointer-right';
    } else if (spaceBottom >= modalHeight + padding) {
      // Modal is below, pointer points up
      pointerStyle.left = `${rect.left + rect.width / 2 - 4}px`; // 4px = half pointer width (smaller pointer)
      pointerStyle.top = `${rect.bottom + 8}px`; // Just touching the highlight border (8px border)
      pointerClass = 'pointer-up';
    } else if (spaceTop >= modalHeight + padding) {
      // Modal is above, pointer points down
      pointerStyle.left = `${rect.left + rect.width / 2 - 4}px`; // 4px = half pointer width (smaller pointer)
      pointerStyle.top = `${rect.top - 8 - 10}px`; // Just touching the highlight border (8px border + 10px pointer height)
      pointerClass = 'pointer-down';
    } else {
      // Center modal, no pointer
      return { display: 'none' };
    }

    return { style: pointerStyle, className: pointerClass };
  };

  useEffect(() => {
    // Manage body scroll based on tutorial state
    if (!isActive) {
      // Re-enable scrolling when tutorial is not active
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isActive]);

  // Auto-scroll and focus management with retry for TutorialStep components
  useEffect(() => {
    if (currentStepData && isActive) {
      console.log(`Tutorial Step ${currentStep + 1}: ${currentStepData.title}`);

      // Show loading animation when starting new step
      setShowLoadingAnimation(true);

      // Disable body scroll during tutorial
      document.body.style.overflow = 'hidden';

      const findElementWithRetry = (retryCount = 0) => {
        const element = getCurrentElement();

        if (element) {
          console.log('Found element:', element);

          // Scroll element into view and focus
          const scrollIntoViewWithFocus = () => {
            // Hide highlighter and set scrolling state for all steps
            setIsScrolling(true);
            setShowHighlighter(false);

            // Always scroll to element regardless of current viewport position
            // This ensures proper positioning when navigating back/forward in tutorial
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });

            // Fast scroll completion detection
            let scrollTimeout: NodeJS.Timeout;
            const handleScrollEnd = () => {
              clearTimeout(scrollTimeout);
              // Give extra time for the last step to settle
              const isLastStep = currentStep === steps.length - 1;
              const scrollEndDelay = isLastStep ? 150 : 50; // Extended delay for last step

              scrollTimeout = setTimeout(() => {
                setIsScrolling(false);

                // Add focus to the element for screen readers
                if (element.tabIndex === -1) {
                  element.tabIndex = -1;
                }
                element.focus({ preventScroll: true });

                console.log(
                  'Scroll completed and element focused',
                  isLastStep ? '(last step)' : ''
                );

                // Remove scroll listener
                window.removeEventListener('scroll', handleScrollEnd);
              }, scrollEndDelay);
            };

            // Listen for scroll events to detect completion
            window.addEventListener('scroll', handleScrollEnd);

            // Much faster fallback timeout
            // Give extra time for the last step fallback
            const isIPad = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isLastStep = currentStep === steps.length - 1;
            let fallbackDelay = isIPad ? 600 : 400; // Base delay

            if (isLastStep) {
              fallbackDelay = isIPad ? 1000 : 700; // Extended fallback for last step
            }

            const fallbackTimeout = setTimeout(() => {
              setIsScrolling(false);
              if (element.tabIndex === -1) {
                element.tabIndex = -1;
              }
              element.focus({ preventScroll: true });
              console.log(
                'Scroll completion detected via fallback timeout',
                isLastStep ? '(last step)' : ''
              );
              window.removeEventListener('scroll', handleScrollEnd);
            }, fallbackDelay);

            return () => {
              clearTimeout(scrollTimeout);
              clearTimeout(fallbackTimeout);
              window.removeEventListener('scroll', handleScrollEnd);
            };
          };

          // Immediate execution for faster response
          scrollIntoViewWithFocus();
        } else if (retryCount < 5) {
          // Retry up to 5 times with increasing delays for TutorialStep components that might not be registered yet
          console.log(`Element not found, retrying... (${retryCount + 1}/5)`);
          setTimeout(
            () => findElementWithRetry(retryCount + 1),
            200 * (retryCount + 1)
          );
        } else {
          console.warn(
            `Could not find element for step: ${currentStepData.title}`
          );
        }
      };

      findElementWithRetry();
    }
  }, [currentStep, currentStepData, isActive]);

  if (!isActive || !currentStepData) {
    return (
      <TutorialContext.Provider
        value={{ currentStepId: null, registerStep, unregisterStep }}
      >
        {children}
      </TutorialContext.Provider>
    );
  }

  return (
    <TutorialContext.Provider
      value={{
        currentStepId: currentStepData.id,
        registerStep,
        unregisterStep,
      }}
    >
      {children}
      {createPortal(
        <>
          {/* Custom CSS Animations */}
          <style>
            {`
              @keyframes modalSlideIn {
                0% {
                  opacity: 0;
                  transform: translateY(20px) scale(0.95);
                }
                50% {
                  opacity: 1;
                }
                100% {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
              
              @keyframes spotlightPulse {
                0%, 100% {
                  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
                }
                50% {
                  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
                }
              }
              
              @keyframes highlightGlow {
                0%, 100% {
                  box-shadow: 
                    0 0 0 9999px rgba(0, 0, 0, 0.6),
                    0 0 20px rgba(59, 130, 246, 0.5),
                    0 0 40px rgba(59, 130, 246, 0.3),
                    0 0 60px rgba(59, 130, 246, 0.1),
                    inset 0 0 0 3px rgba(59, 130, 246, 0.3);
                }
                50% {
                  box-shadow: 
                    0 0 0 9999px rgba(0, 0, 0, 0.6),
                    0 0 30px rgba(59, 130, 246, 0.8),
                    0 0 60px rgba(59, 130, 246, 0.6),
                    0 0 90px rgba(59, 130, 246, 0.3),
                    inset 0 0 0 3px rgba(59, 130, 246, 0.6);
                }
              }
              
              @keyframes shine {
                0% {
                  transform: translateX(-100%) rotate(35deg);
                }
                100% {
                  transform: translateX(100%) rotate(35deg);
                }
              }
              
              @keyframes pointerPulse {
                0%, 100% {
                  opacity: 1;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.7;
                  transform: scale(1.1);
                }
              }
              

            `}
          </style>
          <div className="fixed inset-0 z-[9999]">
            {/* Loading Animation - smaller dots */}
            {showLoadingAnimation && (
              <div className="absolute inset-0 bg-gray-500 bg-opacity-40 flex items-center justify-center z-[10003]">
                <div className="flex space-x-1.5">
                  <div
                    className="w-2.5 h-2.5 bg-red-600 rounded-full animate-bounce"
                    style={{ animationDelay: '0s' }}
                  />
                  <div
                    className="w-2.5 h-2.5 bg-red-600 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <div
                    className="w-2.5 h-2.5 bg-red-600 rounded-full animate-bounce"
                    style={{ animationDelay: '0.4s' }}
                  />
                </div>
              </div>
            )}

            {/* Spotlight circle with box-shadow cutout and glow effect */}
            {currentElement && showHighlighter && (
              <>
                <div
                  className="absolute rounded-xl transition-all duration-200 ease-out"
                  style={{
                    left: currentElement.getBoundingClientRect().left - 8,
                    top: currentElement.getBoundingClientRect().top - 8,
                    width: currentElement.getBoundingClientRect().width + 16,
                    height: currentElement.getBoundingClientRect().height + 16,
                    zIndex: 10000,
                    pointerEvents: 'none',
                    background: 'transparent',
                    animation: 'highlightGlow 2s ease-in-out infinite',
                  }}
                />
                {/* Shine effect overlay */}
                <div
                  className="absolute rounded-xl overflow-hidden"
                  style={{
                    left: currentElement.getBoundingClientRect().left - 8,
                    top: currentElement.getBoundingClientRect().top - 8,
                    width: currentElement.getBoundingClientRect().width + 16,
                    height: currentElement.getBoundingClientRect().height + 16,
                    zIndex: 10001,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)',
                      animation: 'shine 2.5s ease-in-out infinite',
                      animationDelay: '0.5s',
                    }}
                  />
                </div>
              </>
            )}

            {/* Pointer/Arrow pointing to highlighted element - smaller size */}
            {'style' in pointerConfig && showHighlighter && (
              <div
                className="transition-all duration-200 ease-out transform animate-pulse"
                style={{
                  ...pointerConfig.style,
                  width: 0,
                  height: 0,
                  filter: 'drop-shadow(0 2px 4px rgb(0 0 0 / 0.1))',
                  animation: 'pointerPulse 2s ease-in-out infinite',
                  ...(pointerConfig.className === 'pointer-left' && {
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '10px solid black',
                  }),
                  ...(pointerConfig.className === 'pointer-right' && {
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderLeft: '10px solid black',
                  }),
                  ...(pointerConfig.className === 'pointer-up' && {
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderBottom: '10px solid black',
                  }),
                  ...(pointerConfig.className === 'pointer-down' && {
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '10px solid black',
                  }),
                }}
              />
            )}

            {/* Modal */}
            {showModal && (
              <div
                ref={modalRef}
                className="absolute bg-white rounded-xl shadow-2xl z-[10001] overflow-hidden transform transition-all duration-500 ease-out border-4 border-black backdrop-blur-sm animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2"
                style={{
                  ...modalPosition,
                  width: `${modalDimensions.width}px`,
                  maxWidth: `${modalDimensions.width}px`,
                  filter: 'drop-shadow(0 25px 25px rgb(0 0 0 / 0.15))',
                  animation: 'modalSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                {/* Image section - full width - even smaller height ratio */}
                <div className="w-full overflow-hidden rounded-t-xl relative">
                  <div
                    className="w-full flex items-center justify-center overflow-hidden"
                    style={{
                      width: `${modalDimensions.width}px`,
                      height: `${Math.floor(modalDimensions.width * 0.4)}px`, // Further reduced for more compact modal
                      aspectRatio: '7/3',
                    }}
                  >
                    {currentStepData.image ? (
                      <img
                        src={currentStepData.image}
                        alt={currentStepData.title}
                        className="w-full h-full object-cover transition-transform duration-300"
                        style={{
                          width: `${modalDimensions.width}px`,
                          height: `${Math.floor(modalDimensions.width * 0.4)}px`,
                          aspectRatio: '7/3',
                        }}
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          (e.target as HTMLImageElement).style.display = 'none';
                          const placeholder = (
                            e.target as HTMLImageElement
                          ).parentElement?.querySelector('.image-placeholder');
                          if (placeholder) {
                            (placeholder as HTMLElement).style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className={`flex flex-col items-center justify-center text-gray-400 image-placeholder bg-gradient-to-br from-gray-50 to-gray-100 ${currentStepData.image ? 'hidden' : 'flex'}`}
                      style={{
                        width: `${modalDimensions.width}px`,
                        height: `${Math.floor(modalDimensions.width * 0.4)}px`,
                        aspectRatio: '7/3',
                      }}
                    >
                      <svg
                        className={`mb-0.5 ${modalDimensions.width < 220 ? 'w-4 h-4' : modalDimensions.width < 250 ? 'w-5 h-5' : modalDimensions.width < 300 ? 'w-5 h-5' : 'w-6 h-6'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span
                        className={`${modalDimensions.width < 220 ? 'text-xs' : 'text-xs'}`}
                      >
                        Tutorial Image
                      </span>
                    </div>
                  </div>

                  {/* Close button on top right of image - very compact */}
                  <button
                    onClick={handleSkip}
                    className={`absolute flex items-center justify-center z-20 rounded-full transition-all duration-300 ease-out transform hover:scale-110 ${modalDimensions.width < 220 ? 'w-4 h-4 top-1 right-1' : modalDimensions.width < 250 ? 'w-5 h-5 top-1 right-1' : modalDimensions.width < 300 ? 'w-5 h-5 top-1.5 right-1.5' : 'w-6 h-6 top-2 right-2'}`}
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                    aria-label="Close tutorial"
                  >
                    <svg
                      className={`text-white ${modalDimensions.width < 220 ? 'w-2 h-2' : modalDimensions.width < 250 ? 'w-2.5 h-2.5' : modalDimensions.width < 300 ? 'w-2.5 h-2.5' : 'w-3 h-3'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Content area with white background */}
                <div
                  className={`bg-white pb-0 ${modalDimensions.width < 300 ? 'p-3' : modalDimensions.width < 350 ? 'p-4' : 'p-6'}`}
                ></div>

                {/* Bottom section with dark background matching screenshots - compact padding */}
                <div
                  className={`bg-gray-800 text-white ${modalDimensions.width < 220 ? 'p-2' : modalDimensions.width < 250 ? 'p-2.5' : modalDimensions.width < 300 ? 'p-3' : 'p-3.5'}`}
                >
                  {/* Title and Description - very compact text and gaps */}
                  <h3
                    className={`font-medium text-white ${modalDimensions.width < 220 ? 'text-sm mb-1' : modalDimensions.width < 250 ? 'text-base mb-1' : modalDimensions.width < 300 ? 'text-base mb-1.5' : 'text-lg mb-1.5'}`}
                  >
                    {currentStepData.title}
                  </h3>
                  <p
                    className={`text-gray-300 leading-tight ${modalDimensions.width < 220 ? 'text-xs mb-2' : modalDimensions.width < 250 ? 'text-xs mb-2' : modalDimensions.width < 300 ? 'text-xs mb-2.5' : 'text-sm mb-3'}`}
                  >
                    {currentStepData.description}
                  </p>

                  {/* Step indicator - very compact dots and gaps */}
                  <div
                    className={`flex justify-between items-center ${modalDimensions.width < 220 ? 'mb-2' : modalDimensions.width < 250 ? 'mb-2' : modalDimensions.width < 300 ? 'mb-2.5' : 'mb-3'}`}
                  >
                    <div
                      className={`flex ${modalDimensions.width < 220 ? 'space-x-0.5' : modalDimensions.width < 250 ? 'space-x-1' : 'space-x-1'}`}
                    >
                      {steps.map((_, index) => (
                        <div
                          key={index}
                          className={`rounded-full transition-all duration-300 ${modalDimensions.width < 220 ? 'w-1 h-1' : modalDimensions.width < 250 ? 'w-1.5 h-1.5' : 'w-1.5 h-1.5'} ${
                            index === currentStep
                              ? `bg-red-500 ${modalDimensions.width < 220 ? 'w-4' : modalDimensions.width < 250 ? 'w-4' : 'w-5'}`
                              : index < currentStep
                                ? 'bg-green-500'
                                : 'bg-gray-500'
                          }`}
                        />
                      ))}
                    </div>
                    <span
                      className={`text-gray-300 font-medium ${modalDimensions.width < 220 ? 'text-xs' : 'text-xs'}`}
                    >
                      {currentStep + 1} of {steps.length}
                    </span>
                  </div>

                  {/* Buttons - very compact padding */}
                  <div
                    className={`flex ${currentStep === 0 ? 'justify-end' : 'justify-between'} ${modalDimensions.width < 220 ? 'gap-1' : modalDimensions.width < 250 ? 'gap-1.5' : 'gap-2'}`}
                  >
                    {currentStep > 0 && (
                      <button
                        onClick={handleBack}
                        className={`font-medium border border-white rounded transition-all duration-300 ease-out text-white hover:bg-white hover:text-gray-800 ${modalDimensions.width < 220 ? 'px-2 py-1 text-xs' : modalDimensions.width < 250 ? 'px-2.5 py-1 text-xs' : modalDimensions.width < 300 ? 'px-3 py-1.5 text-xs' : 'px-4 py-1.5 text-sm'}`}
                      >
                        {buttonTexts.back}
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      className={`bg-red-600 text-white font-medium rounded hover:bg-red-700 transition-all duration-300 ease-out flex items-center ${modalDimensions.width < 220 ? 'px-2 py-1 text-xs gap-0.5' : modalDimensions.width < 250 ? 'px-2.5 py-1 text-xs gap-1' : modalDimensions.width < 300 ? 'px-3 py-1.5 text-xs gap-1' : 'px-4 py-1.5 text-sm gap-1'}`}
                    >
                      {currentStep === steps.length - 1
                        ? buttonTexts.complete
                        : buttonTexts.next}
                      {currentStep < steps.length - 1 && (
                        <svg
                          className={`${modalDimensions.width < 220 ? 'w-2.5 h-2.5' : modalDimensions.width < 250 ? 'w-3 h-3' : 'w-3 h-3'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </TutorialContext.Provider>
  );
};

export default InteractiveTutorial;
